/**
 * Mutations du pipeline de génération d'exercices (admin).
 *
 * Toutes les écritures passent par des edge functions (RLS + logique métier
 * + appels LLM côté serveur). Les hooks invalident les queries concernées.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/** Invoque une edge function du pipeline et remonte un message d'erreur lisible. */
async function invokePipelineFn<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, {
    body: body as Record<string, unknown>,
  });
  if (error) {
    let message = error.message;
    try {
      const ctxBody = await (
        error as { context?: { json?: () => Promise<unknown> } }
      ).context?.json?.();
      const errObj = ctxBody as { error?: string; message?: string } | undefined;
      if (errObj?.message) message = errObj.message;
      else if (errObj?.error) message = errObj.error;
    } catch {
      // garde le message générique
    }
    throw new Error(message);
  }
  if (data == null) throw new Error('Réponse vide de la fonction.');
  return data;
}

export interface LaunchPipelineInput {
  themeIds: string[];
  particularContext?: string | null;
  parallelLimit: number;
  difficultyDistribution: { easy: number; medium: number; hard: number };
  maxIterationsStatement: number;
  maxIterationsSolution: number;
  /** Modèle Anthropic choisi par étape IA ({ étape -> identifiant_modèle }). */
  stepModels?: Record<string, string>;
}

/** Étape 1 — lance un run. */
export function useLaunchPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LaunchPipelineInput) =>
      invokePipelineFn<{ runId: string }>('pipeline-launch', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
    },
  });
}

export type ScenarioAction = 'validate' | 'edit' | 'regenerate' | 'discard';

export interface UpdateScenarioInput {
  /** Sert à invalider la liste des scénarios du bon run. */
  runId: string;
  scenarioId: string;
  action: ScenarioAction;
  edits?: { title?: string; narrative_pitch?: string; central_question?: string };
  instructions?: string;
}

/** Étape 4 — valide / modifie / régénère / supprime un scénario. */
export function useUpdateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId: _runId, ...payload }: UpdateScenarioInput) =>
      invokePipelineFn<{ ok: boolean; status: string }>('pipeline-update-scenario', payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-scenarios', vars.runId] });
    },
  });
}

/** Étape 5 — lance le matching curriculum pour un run. */
export function useStartMatching() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      invokePipelineFn<{ ok: boolean }>('pipeline-match-curriculum', { runId }),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: ['pipeline-run', runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
    },
  });
}

export interface MatchSelection {
  matchId: string;
  action: 'select' | 'discard';
  overriddenScores?: { relevance?: number; interest?: number };
  overriddenDifficulty?: 'easy' | 'medium' | 'hard';
}

/** Reprend un run en échec à la phase où il s'est arrêté. */
export function useResumeRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      invokePipelineFn<{ ok: boolean; resumedTo: string }>('pipeline-resume', { runId }),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: ['pipeline-run', runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
      qc.invalidateQueries({ queryKey: ['pipeline-jobs', runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-scenarios', runId] });
    },
  });
}

/** Modifie le parallélisme d'un run à tout moment. */
export function useSetParallelLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, parallelLimit }: { runId: string; parallelLimit: number }) =>
      invokePipelineFn<{ ok: boolean; parallelLimit: number }>('pipeline-set-parallel-limit', {
        runId,
        parallelLimit,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-run', vars.runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
    },
  });
}

/** Annule un run en cours. */
export function useCancelRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => invokePipelineFn<{ ok: boolean }>('pipeline-cancel', { runId }),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: ['pipeline-run', runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
      qc.invalidateQueries({ queryKey: ['pipeline-jobs', runId] });
    },
  });
}

/** Relance manuelle d'un job `failed` / `abandoned`, avec surcharges optionnelles. */
export function useRetryJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      overrides,
    }: {
      runId: string;
      jobId: string;
      overrides?: Record<string, unknown>;
    }) => invokePipelineFn<{ ok: boolean }>('pipeline-retry-job', { jobId, overrides }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-jobs', vars.runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-run', vars.runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
    },
  });
}

/**
 * Relecture finale — publie un exercice généré : génère son illustration
 * (style manga), passe l'exercice `published` et notifie l'admin par mail.
 */
export function usePublishExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ exerciseId }: { runId: string; exerciseId: string }) =>
      invokePipelineFn<{ ok: boolean; illustrationUrl: string }>('pipeline-publish-exercise', {
        exerciseId,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-review-exercises', vars.runId] });
    },
  });
}

/** Relecture finale — rejette un exercice généré (`awaiting_review` → `archived`). */
export function useRejectExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ exerciseId }: { runId: string; exerciseId: string; reason?: string }) => {
      const { error } = await supabase
        .from('exercises')
        .update({ status: 'archived' })
        .eq('id', exerciseId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-review-exercises', vars.runId] });
    },
  });
}

/** Étape 6 — confirme les matchings sélectionnés et lance la génération. */
export function useConfirmMatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { runId: string; selections: MatchSelection[] }) =>
      invokePipelineFn<{ ok: boolean; jobCount: number; status: string }>(
        'pipeline-confirm-matches',
        input,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-run', vars.runId] });
      qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
      qc.invalidateQueries({ queryKey: ['pipeline-matches', vars.runId] });
    },
  });
}
